import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Plus } from 'lucide-react';
import { PageHeader, Stat, Stats, DataTable } from '../../components/office';
import { transfersApi, stockApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { qty } from '../../lib/units';

// Transfers (Back office sweep, Oct 2026). Stock moved from one place to another: the bar
// borrowing from the kitchen, or one site helping out another. It comes off the item it left;
// Undo puts it back.

const when = (iso) => new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const EMPTY = { stockId: '', quantity: '', fromSite: '', toSite: '' };

const TransfersPage = () => {
  const [transfers, setTransfers] = useState(null);
  const [stock, setStock] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => transfersApi.list().then((r) => setTransfers(r.data?.transfers || []))
    .catch(() => { setTransfers([]); toast.error("Couldn't load transfers"); });
  const loadStock = () => stockApi.getAll().then((r) => setStock(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadStock(); }, []);

  const item = stock.find((s) => s.id === form.stockId);
  const amount = Number(form.quantity) || 0;

  const save = async () => {
    if (!item) { toast.error('Pick what moved'); return; }
    if (!(amount > 0)) { toast.error('How much?'); return; }
    setSaving(true);
    try {
      await transfersApi.create({ stock_id: item.id, quantity: amount, from_site: form.fromSite.trim(), to_site: form.toSite.trim() });
      toast.success(`${qty(amount, item.unit)} of ${item.name} moved`);
      setOpen(false);
      setForm(EMPTY);
      load(); loadStock();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Couldn't move it");
    } finally { setSaving(false); }
  };

  const undo = async (t) => {
    try { await transfersApi.undo(t.id); toast.success(`Undone: ${t.stock_name} is back`); load(); loadStock(); }
    catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : "Couldn't undo it"); }
  };

  const all = transfers || [];
  const columns = [
    { key: 'created_at', label: 'When', sortable: true, muted: true, className: 'whitespace-nowrap', render: (t) => when(t.created_at) },
    { key: 'stock_name', label: 'Item', sortable: true, render: (t) => <span className="font-medium text-ink">{t.stock_name}</span> },
    { key: 'quantity', label: 'How much', mono: true, render: (t) => qty(t.quantity, t.unit) },
    { key: 'route', label: 'From → to', render: (t) => (
      <span className="inline-flex items-center gap-1.5 text-ink-soft">{t.from_site || '—'}<ArrowRight size={13} className="text-ink-faint" />{t.to_site || '—'}</span>
    ) },
    { key: 'created_by', label: 'By', muted: true },
    { key: 'status', label: 'Status', render: (t) => (t.status === 'undone' ? <Signal tone="info" dot={false}>undone</Signal> : <Signal tone="ok" dot={false}>moved</Signal>) },
  ];

  return (
    <div className="space-y-5" data-testid="transfers-page">
      <PageHeader
        title="Transfers"
        purpose="Stock moved from one place to another, and the cost that moved with it."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="transfer-add"><Plus size={16} className="mr-2" />Move stock</Button>}
      />

      <Stats>
        <Stat label="Transfers" value={transfers ? all.length : null} testId="transfers-count" />
        <Stat label="Standing" value={transfers ? all.filter((t) => t.status !== 'undone').length : null} hint="Not undone" />
      </Stats>

      <DataTable
        columns={columns} rows={all} rowKey="id" loading={transfers === null}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        rowTestId={(t) => `transfer-row-${t.id}`}
        actions={(t) => (t.status !== 'undone'
          ? <Button variant="ghost" size="sm" className="h-8 text-ink-soft" onClick={() => undo(t)} data-testid={`transfer-undo-${t.id}`}>Undo</Button>
          : null)}
        empty={{ word: 'Transfers', title: 'Nothing moved yet', children: 'When the bar borrows from the kitchen, or one site helps another, move it here so both counts stay right.' }}
        testId="transfers-table"
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="form" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle>Move stock</DialogTitle>
            <DialogDescription>It comes off the item where it was. Undo puts it back.</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">What</Label>
              <Select value={form.stockId} onValueChange={(id) => setForm({ ...form, stockId: id })}>
                <SelectTrigger data-testid="transfer-item"><SelectValue placeholder="Pick an item" /></SelectTrigger>
                <SelectContent>{stock.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {qty(s.quantity, s.unit)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">How much</Label>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" step="any" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-28 font-mono tabular-nums" placeholder="0" data-testid="transfer-qty-input" />
                {item && <span className="text-[13px] text-ink-soft">{item.unit === 'each' ? '' : item.unit} of {qty(item.quantity, item.unit)}</span>}
              </div>
              {item && amount > Number(item.quantity || 0) && <p className="text-[12.5px] text-signal-stop">That's more than there is.</p>}
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-2">
              <div className="space-y-1.5"><Label className="text-[12.5px] text-ink-soft">From</Label>
                <Input value={form.fromSite} onChange={(e) => setForm({ ...form, fromSite: e.target.value })} placeholder="e.g. Kitchen" maxLength={120} data-testid="transfer-from-site" /></div>
              <ArrowRight size={16} className="mb-3 text-ink-faint" />
              <div className="space-y-1.5"><Label className="text-[12.5px] text-ink-soft">To</Label>
                <Input value={form.toSite} onChange={(e) => setForm({ ...form, toSite: e.target.value })} placeholder="e.g. Bar" maxLength={120} data-testid="transfer-to-site" /></div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="transfer-save">{saving ? 'Moving…' : 'Move it'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransfersPage;
